/**
 * App — Root component with routing.
 *
 * Public routes: homepage, pricing, faq, contact, login (no auth required).
 * App routes: require auth + household membership.
 *   - /app = Dashboard with 3-tab workflow (home)
 *   - /app/profile, /app/users, /app/recipes, /app/settings, /app/support = dropdown pages
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { usePageTracking } from './hooks/usePageTracking';

// Public pages
import HomePage from './pages/public/HomePage';
import LoginPage from './pages/public/LoginPage';
import PricingPage from './pages/public/PricingPage';
import FaqPage from './pages/public/FaqPage';
import ContactPage from './pages/public/ContactPage';
import TermsPage from './pages/public/TermsPage';
import PrivacyPage from './pages/public/PrivacyPage';
import AboutPage from './pages/public/AboutPage';

// Onboarding
import CreateHouseholdPage from './pages/onboarding/CreateHouseholdPage';

// App pages
import DashboardPage from './pages/app/DashboardPage';
import ProfilePage from './pages/app/ProfilePage';
import UsersPage from './pages/app/UsersPage';
import RecipesPage from './pages/app/RecipesPage';
import SettingsPage from './pages/app/SettingsPage';
import SupportPage from './pages/app/SupportPage';
import AccountPage from './pages/app/AccountPage';
import SubscribePage from './pages/app/SubscribePage';
import ReferralPage from './pages/app/ReferralPage';

// Accept invite
import AcceptInvitePage from './pages/accept-invite/AcceptInvitePage';

// Admin — lazy-loaded so the admin bundle doesn't ship to non-admin users
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminUserSearch = React.lazy(() => import('./pages/admin/AdminUserSearch'));
const AdminHouseholdDetail = React.lazy(() => import('./pages/admin/AdminHouseholdDetail'));
const AdminInviteHistory = React.lazy(() => import('./pages/admin/AdminInviteHistory'));
const AdminSubscription = React.lazy(() => import('./pages/admin/AdminSubscription'));
const AdminAnalytics = React.lazy(() => import('./pages/admin/AdminAnalytics'));

// Layout
import PublicLayout from './components/layout/PublicLayout';
import AppShell from './components/layout/AppShell';
import ProtectedRoute from './components/layout/ProtectedRoute';
import StagingBanner from './components/common/StagingBanner';

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  usePageTracking();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: 'Inter, sans-serif', color: '#6B7280',
      }}>
        Loading...
      </div>
    );
  }
  
  const getPostLoginRedirect = (): string => {
    const pendingInvite = sessionStorage.getItem('pendingInvite');
    if (pendingInvite) {
      return `/accept-invite/${pendingInvite}`;
    }
    return '/app';
  };

  return (
    <>
    <StagingBanner />
    <Routes>
      {/* Public routes — marketing pages */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/login" element={
          isAuthenticated ? <Navigate to={getPostLoginRedirect()} replace /> : <LoginPage />
        } />
        <Route path="/signup" element={
          isAuthenticated ? <Navigate to={getPostLoginRedirect()} replace /> : <LoginPage />
        } />
      </Route>

      {/* Accept invitation */}
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />

      {/* Onboarding — authenticated but no household */}
      <Route element={<ProtectedRoute />}>
        <Route path="/onboarding/create-household" element={<CreateHouseholdPage />} />
      </Route>

      {/* Authenticated app routes */}
      <Route element={<ProtectedRoute requireHousehold />}>
        <Route element={<AppShell />}>
          {/* Home = Dashboard with tab workflow */}
          <Route path="/app" element={<DashboardPage />} />

          {/* Dropdown menu pages */}
          <Route path="/app/profile" element={<ProfilePage />} />
          <Route path="/app/users" element={<UsersPage />} />
          <Route path="/app/recipes" element={<RecipesPage />} />
          <Route path="/app/settings" element={<SettingsPage />} />
          <Route path="/app/support" element={<SupportPage />} />
          <Route path="/app/account" element={<AccountPage />} />
          <Route path="/app/subscribe" element={<SubscribePage />} />
          <Route path="/app/referral" element={<ReferralPage />} />
        </Route>
      </Route>

      {/* Admin dashboard — authenticated, admin group required.
          Wrapped in Suspense because admin routes are lazy-loaded. */}
      <Route element={<ProtectedRoute />}>
        <Route element={
          <React.Suspense fallback={
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100vh', fontFamily: 'Inter, sans-serif', color: '#6B7280',
            }}>
              Loading admin…
            </div>
          }>
            <AdminLayout />
          </React.Suspense>
        }>
          <Route path="/admin" element={<AdminUserSearch />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/households/:householdId" element={<AdminHouseholdDetail />} />
          <Route path="/admin/invites/:householdId" element={<AdminInviteHistory />} />
          <Route path="/admin/subscription/:householdId" element={<AdminSubscription />} />
        </Route>
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

export default App;